import React, { useState, useEffect } from 'react';
import AIUsagePanel from './AIUsagePanel';
import SettingsHub from './SettingsHub';
import axios from 'axios';
import Header from './Header';
import { API_URL } from '../config/api';

const API_BASE = API_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => { 
    loadData();
    loadCurrentUser();
  }, []);
  
  const loadCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Try to decode JWT for user info (no signature verification needed client-side)
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUser(payload);
    } catch (err) {
      console.error('Failed to decode current user:', err);
    }
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, firmsRes] = await Promise.all([
        axios.get(`${API_BASE}/users/admin/all`, { headers }),
        axios.get(`${API_BASE}/firms/admin-list`, { headers })
      ]);
      setUsers(usersRes.data);
      setFirms(firmsRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      alert('Failed to load user data');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.name}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_BASE}/users/admin/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('User deleted');
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleSetFirmType = async (firmId, firmType) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE}/firms/${firmId}/type`, { firm_type: firmType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      alert('Failed to update firm type');
    }
  };

  // Group users by company.
  //
  // Non-admin users with no firm_id used to be dropped here entirely: they
  // aren't in systemUsers (that needs user_type === 'admin') and they had no
  // group to land in, so an invited user created without a firm was invisible
  // in every tab. They now collect under a "No organization" group, which is
  // also a useful signal that someone needs assigning.
  const systemUsers = users.filter(u => u.user_type === 'admin');
  const companyGroups = {};
  const NO_FIRM = '__none__';
  users.forEach(u => {
    if (u.user_type === 'admin') return;
    const key = u.firm_id || NO_FIRM;
    if (!companyGroups[key]) {
      companyGroups[key] = {
        firm_id: u.firm_id || null,
        firm_name: u.firm_id ? (u.firm_name || `Firm ${u.firm_id}`) : 'No organization',
        firm_type: u.firm_type,
        users: []
      };
    }
    companyGroups[key].users.push(u);
  });

  const recruitmentGroups = Object.values(companyGroups).filter(g => g.firm_type === 'recruitment');
  const clientGroups = Object.values(companyGroups).filter(g => g.firm_type === 'client');
  const unassignedGroups = Object.values(companyGroups).filter(g => !g.firm_type);

  const getVisibleGroups = () => {
    switch (activeTab) {
      case 'recruitment': return recruitmentGroups;
      case 'client': return clientGroups;
      case 'system': return [];
      default: return [...recruitmentGroups, ...clientGroups, ...unassignedGroups];
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        <p className="mt-4 text-gray-600">Loading users...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User & Company Management</h1>
          <p className="text-gray-600 mt-1">Manage companies, user accounts, and permissions</p>
        </div>
        <button onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create User
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total Users" value={users.length} color="gray" />
        <StatCard label="System Admins" value={systemUsers.length} color="purple" />
        <StatCard label="Recruitment Cos." value={recruitmentGroups.length} color="blue"
          subtext={`${recruitmentGroups.reduce((s, g) => s + g.users.length, 0)} users`} />
        <StatCard label="Client Cos." value={clientGroups.length} color="green"
          subtext={`${clientGroups.reduce((s, g) => s + g.users.length, 0)} users`} />
        <StatCard label="Unassigned" value={unassignedGroups.length} color="orange"
          subtext={unassignedGroups.length > 0 ? 'Set firm type' : ''} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { key: 'all', label: 'All Companies' },
          { key: 'recruitment', label: 'Recruitment' },
          { key: 'client', label: 'Clients' },
          { key: 'system', label: 'System Users' },
          ...(currentUser?.user_type === 'admin' || currentUser?.company_role === 'company_admin' 
            ? [{ key: 'ai_usage', label: 'AI Usage' }] 
            : []),
          // Settings is available to everyone — non-admins get the Personal
          // tier only; admin-scoped tiers are gated inside SettingsHub.
          { key: 'settings', label: 'Settings' }
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <SettingsHub currentUser={currentUser} orgPanel={<OrgSettingsPanel />} />
      )}

      {/* AI Usage Tab — Suggestions spend for this organization, tracked
          separately from the assistant so the two budgets stay legible. */}
      {activeTab === 'ai_usage' && (
        <AIUsagePanel />
      )}

      {/* System Users Section */}
      {activeTab !== 'settings' && activeTab !== 'ai_usage' && (activeTab === 'all' || activeTab === 'system') && systemUsers.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">System Administrators</h2>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{systemUsers.length}</span>
          </div>
          <div className="bg-white rounded-lg border overflow-hidden">
            <UserTable users={systemUsers} onEdit={(u) => { setEditingUser(u); setShowEditModal(true); }}
              onDelete={handleDeleteUser} onRefresh={loadData} />
          </div>
        </div>
      )}

      {/* Company Groups */}
      {activeTab !== 'settings' && activeTab !== 'ai_usage' && getVisibleGroups().map(group => (
        <CompanyGroup key={group.firm_id} group={group} firms={firms}
          onEditUser={(u) => { setEditingUser(u); setShowEditModal(true); }}
          onDeleteUser={handleDeleteUser} onSetFirmType={handleSetFirmType} onRefresh={loadData} />
      ))}

      {activeTab !== 'settings' && activeTab !== 'ai_usage' && getVisibleGroups().length === 0 && activeTab !== 'system' && activeTab !== 'all' && (
        <div className="text-center py-12 text-gray-500">
          No {activeTab} companies found. Create users with a firm assignment to see company groups here.
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal firms={firms} onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); loadData(); }} />
      )}
      {showEditModal && editingUser && (
        <EditUserModal user={editingUser} firms={firms}
          onClose={() => { setShowEditModal(false); setEditingUser(null); }}
          onSuccess={() => { setShowEditModal(false); setEditingUser(null); loadData(); }} />
      )}
    </div>
    </div>
  );
};

// Stat Card
const StatCard = ({ label, value, color, subtext }) => {
  const colors = {
    gray: 'bg-gray-50 text-gray-600',
    purple: 'bg-purple-50 text-purple-600',
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600'
  };
  return (
    <div className={`rounded-lg p-4 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-75">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {subtext && <p className="text-xs opacity-60 mt-0.5">{subtext}</p>}
    </div>
  );
};

// Company Group Card
const CompanyGroup = ({ group, onEditUser, onDeleteUser, onSetFirmType, onRefresh }) => {
  const [expanded, setExpanded] = useState(true);
  const typeColors = {
    recruitment: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '🔍' },
    client: { bg: 'bg-green-100', text: 'text-green-700', icon: '🏢' }
  };
  const typeStyle = typeColors[group.firm_type] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: '❓' };
  const adminCount = group.users.filter(u => u.company_role === 'company_admin').length;

  return (
    <div className="mb-4">
      <div className={`flex items-center gap-3 p-3 bg-white rounded-t-lg border ${expanded ? 'border-b-0' : 'rounded-b-lg'}`}>
        <button onClick={() => setExpanded(!expanded)} className="text-gray-400 hover:text-gray-600">
          <svg className={`w-5 h-5 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <span className="text-lg">{typeStyle.icon}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{group.firm_name}</h3>
            {group.firm_type ? (
              <span className={`text-xs px-2 py-0.5 rounded-full ${typeStyle.bg} ${typeStyle.text}`}>
                {group.firm_type === 'recruitment' ? 'Recruitment' : 'Client'}
              </span>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">No Type Set</span>
                <button onClick={() => onSetFirmType(group.firm_id, 'recruitment')}
                  className="text-xs px-2 py-0.5 rounded border border-blue-300 text-blue-600 hover:bg-blue-50">Set Recruitment</button>
                <button onClick={() => onSetFirmType(group.firm_id, 'client')}
                  className="text-xs px-2 py-0.5 rounded border border-green-300 text-green-600 hover:bg-green-50">Set Client</button>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {group.users.length} user{group.users.length !== 1 ? 's' : ''} · {adminCount} admin{adminCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      {expanded && (
        <div className="bg-white rounded-b-lg border border-t-0 overflow-hidden">
          <UserTable users={group.users} onEdit={onEditUser} onDelete={onDeleteUser} showRole onRefresh={onRefresh} />
        </div>
      )}
    </div>
  );
};

// User Table
const UserTable = ({ users, onEdit, onDelete, showRole = false, onRefresh }) => {
  const getRoleBadge = (user) => {
    if (user.user_type === 'admin') return <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">System Admin</span>;
    if (user.company_role === 'company_admin') return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">Company Admin</span>;
    return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Member</span>;
  };

  const getTypeBadge = (user) => {
    switch (user.user_type) {
      case 'admin': return <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">Admin</span>;
      case 'recruitment': return <span className="px-2 py-0.5 text-xs rounded-full bg-cyan-100 text-cyan-700">Recruiter</span>;
      case 'client': return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Client</span>;
      default: return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{user.user_type}</span>;
    }
  };

  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
          {showRole && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>}
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {users.map(user => (
          <tr key={user.id} className={`hover:bg-gray-50 ${user.is_active === false ? 'opacity-50' : ''}`}>
            <td className="px-4 py-3 whitespace-nowrap">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                  {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-gray-900">{user.name}</span>
              </div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
            <td className="px-4 py-3 whitespace-nowrap">{getTypeBadge(user)}</td>
            {showRole && <td className="px-4 py-3 whitespace-nowrap">{getRoleBadge(user)}</td>}
            <td className="px-4 py-3 whitespace-nowrap">
              <span className={`text-xs px-2 py-0.5 rounded-full ${user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {user.is_active !== false ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-right">
              <button onClick={() => onEdit(user)} className="text-blue-600 hover:text-blue-800 text-sm mr-3">Edit</button>
              <button onClick={() => onDelete(user)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Create User Modal
const CreateUserModal = ({ firms, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '',
    user_type: 'client', firm_id: '', company_role: 'member'
  });
  const [saving, setSaving] = useState(false);

  const needsFirm = formData.user_type === 'client' || formData.user_type === 'recruitment';
  const filteredFirms = firms.filter(f => {
    if (formData.user_type === 'client') return f.firm_type === 'client' || !f.firm_type;
    if (formData.user_type === 'recruitment') return f.firm_type === 'recruitment' || !f.firm_type;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      alert('Please fill in all required fields'); return;
    }
    if (needsFirm && !formData.firm_id) {
      alert('Please select a company'); return;
    }
    if (formData.password.length < 8) {
      alert('Password must be at least 8 characters'); return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API_BASE}/users/admin/create`, {
        ...formData,
        firm_id: formData.firm_id || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      alert('User created successfully!');
      onSuccess();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create user');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">Create New User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Field label="Full Name *">
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="John Doe" />
          </Field>
          <Field label="Email *">
            <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="john@company.com" />
          </Field>
          <Field label="Password *">
            <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Minimum 8 characters" />
          </Field>
          <Field label="User Type *">
            <select value={formData.user_type}
              onChange={(e) => setFormData({...formData, user_type: e.target.value, firm_id: ''})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="client">Client User</option>
              <option value="recruitment">Recruitment User</option>
              <option value="admin">System Admin</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {formData.user_type === 'client' && 'Investment firm / client company user with shared company database'}
              {formData.user_type === 'recruitment' && 'Recruitment company user with shared company candidate database'}
              {formData.user_type === 'admin' && 'Full system administrator with access to all features'}
            </p>
          </Field>
          {needsFirm && (
            <Field label="Company *">
              <select value={formData.firm_id} onChange={(e) => setFormData({...formData, firm_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select a company...</option>
                {filteredFirms.map(f => (
                  <option key={f.id} value={f.id}>{f.name} {f.firm_type ? `(${f.firm_type})` : ''}</option>
                ))}
              </select>
            </Field>
          )}
          {formData.user_type !== 'admin' && needsFirm && (
            <Field label="Company Role">
              <select value={formData.company_role} onChange={(e) => setFormData({...formData, company_role: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="member">Member</option>
                <option value="company_admin">Company Admin</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Company admins have elevated permissions within their organization.
              </p>
            </Field>
          )}
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
            <button type="submit" disabled={saving}
              className={`flex-1 px-4 py-2 rounded-lg font-medium ${saving ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal
const EditUserModal = ({ user, firms, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: user.name, email: user.email,
    user_type: user.user_type, firm_id: user.firm_id || '',
    company_role: user.company_role || 'member'
  });
  const [saving, setSaving] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const needsFirm = formData.user_type === 'client' || formData.user_type === 'recruitment';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) { alert('Name and email are required'); return; }
    if (needsFirm && !formData.firm_id) { alert('Please select a company'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE}/users/admin/${user.id}`, {
        ...formData,
        firm_id: formData.firm_id || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      alert('User updated!');
      onSuccess();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update user');
    } finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) { alert('Password must be at least 8 characters'); return; }
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE}/users/admin/${user.id}/reset-password`, { password: newPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Password reset successfully');
      setShowResetPassword(false);
      setNewPassword('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reset password');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">Edit User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Field label="Full Name *">
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </Field>
          <Field label="Email *">
            <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </Field>
          <Field label="User Type *">
            <select value={formData.user_type}
              onChange={(e) => setFormData({...formData, user_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="client">Client User</option>
              <option value="recruitment">Recruitment User</option>
              <option value="admin">System Admin</option>
            </select>
          </Field>
          <Field label={`Company ${needsFirm ? '*' : '(Optional)'}`}>
            <select value={formData.firm_id} onChange={(e) => setFormData({...formData, firm_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">No company assigned</option>
              {firms.map(f => (
                <option key={f.id} value={f.id}>{f.name} {f.firm_type ? `(${f.firm_type})` : ''}</option>
              ))}
            </select>
          </Field>
          {formData.user_type !== 'admin' && (
            <Field label="Company Role">
              <select value={formData.company_role} onChange={(e) => setFormData({...formData, company_role: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="member">Member</option>
                <option value="company_admin">Company Admin</option>
              </select>
            </Field>
          )}

          {/* Password Reset */}
          <div className="border-t pt-4">
            {!showResetPassword ? (
              <button type="button" onClick={() => setShowResetPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-800">Reset Password</button>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">New Password</label>
                <div className="flex gap-2">
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Min 8 characters" />
                  <button type="button" onClick={handleResetPassword}
                    className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm whitespace-nowrap">Reset</button>
                  <button type="button" onClick={() => { setShowResetPassword(false); setNewPassword(''); }}
                    className="px-3 py-2 bg-gray-200 rounded-lg text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
            <button type="submit" disabled={saving}
              className={`flex-1 px-4 py-2 rounded-lg font-medium ${saving ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Field wrapper
const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

// Org-wide settings panel (admin only)
const OrgSettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cooldownDays, setCooldownDays] = useState(30);
  const [savedMessage, setSavedMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await axios.get(`${API_BASE}/org-settings`, { headers: { Authorization: `Bearer ${token}` } });
        setSettings(res.data);
        setCooldownDays(res.data.linkedin_sync_cooldown_days ?? 30);
      } catch (err) {
        console.error('Failed to load org settings:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSavedMessage('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.put(`${API_BASE}/org-settings`,
        { linkedin_sync_cooldown_days: parseInt(cooldownDays) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(res.data);
      setSavedMessage('Settings saved successfully');
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setSavedMessage('Error: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="animate-pulse text-gray-400 text-sm">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">LinkedIn Sync Settings</h2>
            <p className="text-sm text-gray-500">Configure how often firm data can be re-synced from LinkedIn for your organization.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sync Cooldown (days)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="365"
                value={cooldownDays}
                onChange={(e) => setCooldownDays(e.target.value)}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-sm text-gray-500">days between syncs per firm</span>
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              A firm cannot be re-synced from LinkedIn until this many days have passed since the last sync. 
              Set to <strong>0</strong> to disable the cooldown. Recommended: 7-30 days to control API costs.
            </p>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
              {savedMessage && (
                <span className={`text-sm ${savedMessage.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {savedMessage}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-xs text-blue-900">
            <strong>How it works:</strong> Each LinkedIn sync uses a Bright Data API credit (~$0.0015 per firm).
            The cooldown helps prevent accidental re-syncing and keeps costs predictable.
            Admins can override the cooldown by passing <code className="bg-blue-100 px-1 rounded">force: true</code> on individual syncs.
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
